import React, { useCallback, useEffect, useState, useContext } from 'react'
import useTranslate from '../hooks/useTranslate';
import Context from '../context'
import { useLocation, Link } from 'react-router-dom'
import SummaryApi from '../common'
import VerticalCard from '../components/VerticalCard'
import service from '../common/HttpService'

const SearchProduct = () => {
    const query = useLocation()
    const [data, setData] = useState([])
    const [subcategories, setSubcategories] = useState([])
    const [categories, setCategories] = useState([])
    const [itemTypes, setItemTypes] = useState([])
    const [loading, setLoading] = useState(false)
    const [categorySubcategories, setCategorySubcategories] = useState([]) // subcats fetched for category search
    const [selectedCategoryForSub, setSelectedCategoryForSub] = useState(null) // which category is expanded
    const { translateText, languageCode } = useContext(Context);

    const t = useTranslate({
        searching: "Searching...",
        quickFilters: "Quick Filters",
        productResults: "Product Results",
        found: "Found",
        noProductsMatched: "No products matched your search",
        searchingIn: "Searching in",
        categories: "Categories",
        brands: "Brands",
        subcategories: "Subcategories",
        allProducts: "All Products",
    });

    const [translatedCategories, setTranslatedCategories] = useState([])
    const [translatedSubCategories, setTranslatedSubCategories] = useState([])
    const [translatedItemTypes, setTranslatedItemTypes] = useState([])
    const [translatedResultsTitle, setTranslatedResultsTitle] = useState("Product Results")
    const [translatedCategorySubcategories, setTranslatedCategorySubcategories] = useState([])

    const fetchProduct = useCallback(async () => {
        const URLSearch = new URLSearchParams(query.search)
        const q = URLSearch.get("q")
        const searchType = URLSearch.get("searchType") || 'all'
        const category = URLSearch.get("category")
        const subcat = URLSearch.get("subcat")
        const store = URLSearch.get("store")

        if (!q && !category && !subcat && !store) {
            setData([])
            setSubcategories([])
            setCategories([])
            setItemTypes([])
            return
        }

        try {
            setLoading(true)

            const keywordPayload = new FormData();
            if (q) keywordPayload.append('keyword', q);

            // ── Route API call based on searchType ──────────────────────────
            if (searchType === 'brand') {
                // Search brands by name
                const brandRes = await service.post(SummaryApi.search_brand_by_name.url, keywordPayload).catch(() => ({}))
                if (brandRes && (brandRes.success === 'true' || brandRes.success === true)) {
                    setItemTypes(brandRes.extraData?.brand || []);
                } else {
                    setItemTypes([]);
                }
                setCategories([]);
                setSubcategories([]);
                setData([]);

            } else if (searchType === 'category') {
                // Search categories by name
                const catRes = await service.post(SummaryApi.search_category_by_name.url, keywordPayload).catch(() => ({}))
                const foundCategories = (catRes && (catRes.success === 'true' || catRes.success === true))
                    ? (catRes.extraData?.category || [])
                    : [];
                setCategories(foundCategories);

                // Also fetch subcategories for each found category
                setCategorySubcategories([]);
                setSelectedCategoryForSub(null);
                if (foundCategories.length > 0) {
                    try {
                        const subPromises = foundCategories.map(cat => {
                            const fd = new FormData();
                            fd.append('category_id', cat.id || cat.category_id || '');
                            fd.append('id', cat.id || cat.category_id || '');
                            return service.post(SummaryApi.subcategoryById.url, fd).catch(() => ({}));
                        });
                        const subResponses = await Promise.all(subPromises);
                        const allSubs = [];
                        subResponses.forEach((res, idx) => {
                            if (res && (res.success === 'true' || res.success === true)) {
                                const rawSub = res.extraData?.subcategory;
                                // API can return single object or array
                                const subsArr = Array.isArray(rawSub) ? rawSub : (rawSub ? [rawSub] : []);
                                subsArr.forEach(sub => {
                                    allSubs.push({
                                        ...sub,
                                        _parentCategory: foundCategories[idx]
                                    });
                                });
                            }
                        });
                        // Deduplicate by id
                        const unique = Array.from(new Map(allSubs.map(s => [s.id || s._id, s])).values());
                        setCategorySubcategories(unique);
                    } catch (subErr) {
                        console.error('Error fetching category subcategories:', subErr);
                    }
                }

                setItemTypes([]);
                setSubcategories([]);
                setData([]);

            } else if (searchType === 'subcategory') {
                // Search subcategories by name
                const subcatRes = await service.post(SummaryApi.search_subcategory_by_name.url, keywordPayload).catch(() => ({}))
                if (subcatRes && (subcatRes.success === 'true' || subcatRes.success === true)) {
                    setSubcategories(subcatRes.extraData?.subcategory || []);
                } else {
                    setSubcategories([]);
                }
                setCategories([]);
                setItemTypes([]);
                setData([]);

            } else {
                // Default: search all products + quick-filter links
                const formDataPayload = new FormData();
                if (q) formDataPayload.append('item', q);
                if (category) formDataPayload.append('category', category);
                if (subcat) formDataPayload.append('subcat', subcat);
                if (store) formDataPayload.append('store', store);

                const fetchPromises = [
                    service.post(SummaryApi.search_product.url, formDataPayload)
                ];

                if (q) {
                    fetchPromises.push(
                        service.post(SummaryApi.search_category_by_name.url, keywordPayload).catch(() => ({})),
                        service.post(SummaryApi.search_subcategory_by_name.url, keywordPayload).catch(() => ({})),
                        service.post(SummaryApi.search_brand_by_name.url, keywordPayload).catch(() => ({}))
                    );
                }

                const [productRes, catRes, subcatRes, brandRes] = await Promise.all(fetchPromises);

                if (catRes && (catRes.success === "true" || catRes.success === true)) {
                    setCategories(catRes.extraData?.category || []);
                } else {
                    setCategories([]);
                }

                if (subcatRes && (subcatRes.success === "true" || subcatRes.success === true)) {
                    setSubcategories(subcatRes.extraData?.subcategory || []);
                } else {
                    setSubcategories([]);
                }

                if (brandRes && (brandRes.success === "true" || brandRes.success === true)) {
                    setItemTypes(brandRes.extraData?.brand || []);
                } else {
                    setItemTypes([]);
                }

                if (productRes && (productRes.success === "true" || productRes.success === true)) {
                    let fetchedProducts = productRes.extraData?.items || productRes.extraData?.product || productRes.extraData || productRes.data || []
                    if (fetchedProducts && typeof fetchedProducts === 'object' && !Array.isArray(fetchedProducts)) {
                        fetchedProducts = Object.values(fetchedProducts);
                    }
                    if (!Array.isArray(fetchedProducts)) fetchedProducts = [fetchedProducts];

                    const backendBaseUrl = (SummaryApi.allProduct.url || "").split('/Api')[0];

                    const formatted = fetchedProducts.map(item => {
                        let images = [];
                        const itemImages = item.image || item.productImage || [];
                        if (Array.isArray(itemImages)) {
                            images = itemImages;
                        } else if (typeof itemImages === 'string' && itemImages) {
                            images = itemImages.includes(',') ? itemImages.split(',') : [itemImages];
                        }

                        images = images.map(img => {
                            if (typeof img === 'string' && img && !img.startsWith('http')) {
                                const cleanPath = img.startsWith('/') ? img.substring(1) : img;
                                return `${backendBaseUrl}/${cleanPath}`;
                            } else if (img === "https://www.fasalshop.com/product-images/") {
                                return null;
                            }
                            return img;
                        }).filter(Boolean);

                        if (images.length === 0) images = [''];

                        const firstRate = (item.rates && item.rates[0]) || (item.item_rates && item.item_rates[0]) || {};

                        return {
                            ...item,
                            _id: item.id || item._id,
                            productName: item.itemname || item.productName || "",
                            productImage: images,
                            price: Number(firstRate.mrp) || Number(item.price) || 0,
                            sellingPrice: Number(firstRate.salerate) || Number(item.sellingPrice) || 0,
                            category: item.category_name || item.category,
                            brandName: item.brand_name || item.brandName || "",
                        }
                    })
                    setData(formatted)
                } else {
                    setData([])
                }
            }


        } catch (error) {
            console.log("Error searching:", error)
            setData([])
            setSubcategories([])
            setCategories([])
            setItemTypes([])
        } finally {
            setLoading(false)
        }
    }, [query.search])

    useEffect(() => {
        fetchProduct()
    }, [fetchProduct]);

    useEffect(() => {
        const translateFilters = async () => {
            if (!languageCode || languageCode === 'en') {
                setTranslatedCategories(categories);
                setTranslatedSubCategories(subcategories);
                setTranslatedItemTypes(itemTypes);
                setTranslatedResultsTitle("Product Results");
                setTranslatedCategorySubcategories(categorySubcategories);
                return;
            }
            try {
                const [cats, subs, types, title, catSubs] = await Promise.all([
                    Promise.all(categories.map(async c => ({ ...c, name: await translateText(c?.category_name || c?.name || c, languageCode) }))),
                    Promise.all(subcategories.map(async s => ({ ...s, name: await translateText(s?.subcategory_name || s?.name || s, languageCode) }))),
                    Promise.all(itemTypes.map(async it => ({ ...it, name: await translateText(it?.type_name || it?.name || it, languageCode) }))),
                    translateText("Product Results", languageCode),
                    Promise.all(categorySubcategories.map(async s => ({ ...s, name: await translateText(s?.name || s?.subcategory_name || '', languageCode) })))
                ]);
                setTranslatedCategories(cats);
                setTranslatedSubCategories(subs);
                setTranslatedItemTypes(types);
                setTranslatedResultsTitle(title);
                setTranslatedCategorySubcategories(catSubs);
            } catch (error) {
                console.error("Search filters translation error:", error);
            }
        };
        translateFilters();
    }, [languageCode, categories, subcategories, itemTypes, categorySubcategories, translateText]);

    // Determine active search type label for the UI
    const activeSearchType = new URLSearchParams(query.search).get('searchType') || 'all';
    const searchTypeLabel = {
        brand: t.brands,
        category: t.categories,
        subcategory: t.subcategories,
        all: t.allProducts
    }[activeSearchType] || t.allProducts;

    return (
        <div className='container mx-auto p-4 min-h-[60vh]'>
            {loading ? (
                <div className='space-y-10 animate-pulse'>
                    {/* Active Search Type Badge Skeleton */}
                    <div className='flex items-center gap-3'>
                        <div className='h-4 bg-slate-100 rounded w-20'></div>
                        <div className='h-6 bg-slate-200 rounded-full w-28'></div>
                    </div>

                    {/* Product Results Skeleton */}
                    <div>
                        <div className='flex items-center justify-between mb-6'>
                            <div className='h-6 bg-slate-200 rounded w-48'></div>
                            <div className='h-6 bg-slate-100 rounded-full w-16'></div>
                        </div>
                        <VerticalCard loading={true} />
                    </div>
                </div>
            ) : (
                <div className='space-y-10'>

                    {/* Active Search Type Badge */}
                    {activeSearchType !== 'all' && (
                        <div className='flex items-center gap-3'>
                            <span className='text-xs text-gray-500 font-semibold uppercase tracking-wider'>{t.searchingIn}:</span>
                            <span className='bg-[#143d2c] text-white text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-widest shadow-sm'>
                                {searchTypeLabel}
                            </span>
                        </div>
                    )}

                    {/* Brand Results */}
                    {activeSearchType === 'brand' && (
                        <div className='bg-white rounded-2xl p-6 shadow-sm border border-gray-50'>
                            <h2 className='text-[#143d2c] font-black text-sm uppercase tracking-widest mb-4'>{t.brands}</h2>
                            {translatedItemTypes.length === 0 ? (
                                <p className='text-gray-400 text-sm font-bold'>{t.noProductsMatched}</p>
                            ) : (
                                <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4'>
                                    {translatedItemTypes.map((brand, idx) => (
                                        <Link
                                            key={idx}
                                            to={`/product-category?brand=${itemTypes[idx]?.brand_name || itemTypes[idx]?.name || itemTypes[idx]}&brandId=${itemTypes[idx]?.id || itemTypes[idx]?._id || ''}`}
                                            className='flex flex-col items-center justify-center p-4 bg-white rounded-xl border border-gray-100 hover:border-[#00b259] transition-all'
                                        >
                                            {brand.image && (
                                                <div className='w-full h-20 mb-3 flex items-center justify-center rounded-lg overflow-hidden bg-gray-50'>
                                                    <img src={brand.image} alt={brand.name} className='w-full h-full object-cover' onError={(e) => e.target.style.display = 'none'} />
                                                </div>
                                            )}
                                            <span className='text-xs font-bold text-gray-800 text-center'>{brand.name}</span>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Category Results */}
                    {activeSearchType === 'category' && (
                        <div className='space-y-6'>
                            {/* Categories Grid */}
                            <div className='bg-white rounded-3xl p-6 shadow-sm border border-gray-50'>
                                <div className='flex items-center justify-between mb-6'>
                                    <h2 className='text-[#143d2c] font-black text-sm uppercase tracking-widest flex items-center gap-2'>
                                        <span className='w-2 h-2 rounded-full bg-[#00b259] inline-block'></span>
                                        {t.categories}
                                    </h2>
                                    <span className='text-[10px] font-black text-gray-400 uppercase tracking-widest'>{translatedCategories.length} found</span>
                                </div>
                                {translatedCategories.length === 0 ? (
                                    <div className='flex flex-col items-center justify-center py-8 text-gray-400'>
                                        <p className='text-sm font-bold'>{t.noProductsMatched}</p>
                                    </div>
                                ) : (
                                    <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4'>
                                        {translatedCategories.map((cat, idx) => (
                                            <button
                                                key={idx}
                                                onClick={() => setSelectedCategoryForSub(selectedCategoryForSub === idx ? null : idx)}
                                                className={`flex flex-col bg-white rounded-xl border transition-all overflow-hidden text-left w-full ${selectedCategoryForSub === idx
                                                    ? 'border-[#00b259] ring-1 ring-[#00b259]'
                                                    : 'border-gray-100 hover:border-[#00b259]'
                                                    }`}
                                            >
                                                {/* Image Area */}
                                                {cat.image && (
                                                    <div className='w-full aspect-square bg-gray-50 flex items-center justify-center overflow-hidden'>
                                                        <img
                                                            src={cat.image}
                                                            alt={cat.name}
                                                            className='w-full h-full object-cover'
                                                            onError={(e) => { e.target.style.display = 'none'; }}
                                                        />
                                                    </div>
                                                )}

                                                {/* Name */}
                                                <div className='p-3'>
                                                    <p className='text-sm font-bold text-gray-800 text-center leading-tight line-clamp-2'>
                                                        {cat.name}
                                                    </p>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {/* Direct explore link */}
                                {translatedCategories.length > 0 && (
                                    <div className='mt-4 flex flex-wrap gap-2'>
                                        {translatedCategories.map((cat, idx) => (
                                            <Link
                                                key={idx}
                                                to={`/product-category?category=${categories[idx]?.name || categories[idx]?.category_name || categories[idx]}`}
                                                className='inline-flex items-center gap-1 text-[10px] font-black text-[#00b259] bg-[#00b259]/5 hover:bg-[#00b259]/10 border border-[#00b259]/20 px-3 py-1.5 rounded-xl transition-all uppercase tracking-widest'
                                            >
                                                {cat.name} → View Products
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Subcategory Selection Section - shown when category is selected OR subcats loaded */}
                            {translatedCategorySubcategories.length > 0 && (
                                <div className='bg-white rounded-3xl p-6 shadow-sm border border-gray-50 animate-fadeIn'>
                                    {/* Header */}
                                    <div className='flex items-center justify-between mb-6'>
                                        <div className='flex items-center gap-3'>
                                            <div>
                                                <h3 className='text-[#143d2c] font-black text-sm uppercase tracking-widest'>{t.subcategories}</h3>
                                                <p className='text-[10px] text-gray-400 font-semibold'>
                                                    {selectedCategoryForSub !== null
                                                        ? `${translatedCategorySubcategories.filter(s => s._parentCategory?.id === categories[selectedCategoryForSub]?.id).length} subcategories`
                                                        : `${translatedCategorySubcategories.length} subcategories found`
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                        <span className='text-[10px] font-black text-white bg-[#00b259] px-3 py-1 rounded-full uppercase tracking-widest shadow-sm'>
                                            {translatedCategorySubcategories.length} total
                                        </span>
                                    </div>

                                    {/* Subcategory Cards */}
                                    <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4'>
                                        {(selectedCategoryForSub !== null
                                            ? translatedCategorySubcategories.filter(s => s._parentCategory?.id === categories[selectedCategoryForSub]?.id)
                                            : translatedCategorySubcategories
                                        ).map((sub, idx) => (
                                            <Link
                                                key={sub.id || idx}
                                                to={`/product-category?category=${sub._parentCategory?.name || sub._parentCategory?.category_name || ''}&subcategory=${sub.name || sub.subcategory_name || ''}`}
                                                className='flex flex-col items-center bg-white rounded-xl border border-gray-100 hover:border-[#00b259] transition-all overflow-hidden p-3 text-center'
                                            >
                                                {/* Image */}
                                                {sub.image && (
                                                    <div className='w-full h-16 sm:h-20 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center mb-2'>
                                                        <img
                                                            src={sub.image}
                                                            alt={sub.name}
                                                            className='w-full h-full object-cover'
                                                            onError={(e) => { e.target.style.display = 'none'; }}
                                                        />
                                                    </div>
                                                )}

                                                {/* Name */}
                                                <p className='text-xs font-bold text-gray-800 leading-tight line-clamp-2 mt-1'>
                                                    {sub.name || sub.subcategory_name}
                                                </p>

                                                {/* Parent category pill */}
                                                {sub._parentCategory && (
                                                    <span className='mt-1 text-[10px] text-gray-500 truncate max-w-full'>
                                                        {sub._parentCategory.category_name || sub._parentCategory.name}
                                                    </span>
                                                )}
                                            </Link>
                                        ))}
                                    </div>

                                    {/* Show all toggle if filtered */}
                                    {selectedCategoryForSub !== null && translatedCategorySubcategories.length > translatedCategorySubcategories.filter(s => s._parentCategory?.id === categories[selectedCategoryForSub]?.id).length && (
                                        <button
                                            onClick={() => setSelectedCategoryForSub(null)}
                                            className='mt-4 w-full text-center text-[10px] font-black text-[#00b259] uppercase tracking-widest hover:underline'
                                        >
                                            Show All {translatedCategorySubcategories.length} Subcategories ↓
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Subcategory Results */}
                    {activeSearchType === 'subcategory' && (
                        <div className='bg-white rounded-3xl p-6 shadow-sm border border-gray-50'>
                            <h2 className='text-[#143d2c] font-black text-sm uppercase tracking-widest mb-4'>{t.subcategories}</h2>
                            {translatedSubCategories.length === 0 ? (
                                <p className='text-gray-400 text-sm font-bold'>{t.noProductsMatched}</p>
                            ) : (
                                <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4'>
                                    {translatedSubCategories.map((sub, idx) => (
                                        <Link
                                            key={idx}
                                            to={`/product-category?subcategory=${subcategories[idx]?.subcategory_name || subcategories[idx]?.name || subcategories[idx]}`}
                                            className='flex flex-col items-center justify-center p-4 bg-white rounded-xl border border-gray-100 hover:border-[#00b259] transition-all'
                                        >
                                            {sub.image && (
                                                <div className='w-full h-20 mb-3 flex items-center justify-center rounded-lg overflow-hidden bg-gray-50'>
                                                    <img src={sub.image} alt={sub.name} className='w-full h-full object-cover' onError={(e) => e.target.style.display = 'none'} />
                                                </div>
                                            )}
                                            <span className='text-xs font-bold text-gray-800 text-center'>{sub.name}</span>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* All: Categories, Subcategories & Item Types Quick Links */}
                    {activeSearchType === 'all' && (translatedCategories.length > 0 || translatedSubCategories.length > 0 || itemTypes.length > 0) && (
                        <div className='bg-white rounded-3xl p-6 shadow-sm border border-gray-50'>
                            <h2 className='text-[#143d2c] font-black text-sm uppercase tracking-widest mb-4'>{t.quickFilters}</h2>
                            <div className='flex flex-wrap gap-2'>
                                {translatedCategories.map((cat, idx) => (
                                    <Link
                                        key={idx}
                                        to={`/product-category?category=${categories[idx]?.category_name || categories[idx]?.name || categories[idx]}`}
                                        className='flex items-center gap-2 bg-gray-50 hover:bg-gray-100 text-gray-700 px-4 py-2 rounded-xl text-xs font-bold transition-all border border-gray-100'
                                    >
                                        {cat.image && <img src={cat.image} className='h-5 w-5 object-cover rounded-full' alt={cat.name} onError={(e) => e.target.style.display = 'none'} />}
                                        {cat.name}
                                    </Link>
                                ))}
                                {translatedSubCategories.map((sub, idx) => (
                                    <Link
                                        key={idx}
                                        to={`/product-category?subcategory=${subcategories[idx]?.subcategory_name || subcategories[idx]?.name || subcategories[idx]}`}
                                        className='flex items-center gap-2 bg-gray-50 hover:bg-gray-100 text-gray-700 px-4 py-2 rounded-xl text-xs font-bold transition-all border border-gray-100'
                                    >
                                        {sub.image && <img src={sub.image} className='h-5 w-5 object-cover rounded-full' alt={sub.name} onError={(e) => e.target.style.display = 'none'} />}
                                        {sub.name}
                                    </Link>
                                ))}
                                {translatedItemTypes.map((type, idx) => (
                                    <Link
                                        key={idx}
                                        to={`/product-category?brand=${itemTypes[idx]?.brand_name || itemTypes[idx]?.name || itemTypes[idx]}`}
                                        className='flex items-center gap-2 bg-gray-50 hover:bg-gray-100 text-gray-700 px-4 py-2 rounded-xl text-xs font-bold transition-all border border-gray-100'
                                    >
                                        {type.image && <img src={type.image} className='h-5 w-5 object-cover rounded-full' alt={type.name} onError={(e) => e.target.style.display = 'none'} />}
                                        {type.name}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Product Results - only shown for 'all' search type */}
                    {activeSearchType === 'all' && (
                        <div>
                            <div className='flex items-center justify-between mb-6'>
                                <h2 className='text-[#143d2c] font-black text-xl'>{translatedResultsTitle}</h2>
                                <span className='bg-[#00b259] text-white text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider'>
                                    {data.length} {t.found}
                                </span>
                            </div>

                            {data.length === 0 ? (
                                <div className='bg-white text-center py-20 rounded-3xl border border-dashed border-gray-200'>
                                    <p className='text-gray-400 font-bold'>{t.noProductsMatched}</p>
                                </div>
                            ) : (
                                <VerticalCard loading={loading} data={data} />
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default SearchProduct